import React, { Fragment, useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Banner from '../assets/img/Banner.jpg'
import axios from 'axios'

import {
    Button,
    Table,
} from "reactstrap";

// Import API config object
import { tokenConfig } from '../utils/apiConfig'
import { buildLocationUrl, locationLabel } from '../utils/locationLink'

// Import Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRight,
    faCalendarAlt,
    faEdit,
    faEnvelope,
    faIdCard,
    faLeaf,
    faPhone,
    faTrash,
    faUsers,
} from '@fortawesome/free-solid-svg-icons';

// Import components for Profile Page
import UpdatePhoto from '../components/ProfilePage/UpdatePhoto'
import UpdateDetails from '../components/ProfilePage/UpdateDetails'
import UpcomingEventsGrid from '../components/EventRegister/UpcomingEventsGrid'

const ProfilePage = props => {
    const navigate = useNavigate()
    const userDetails = props.userDetails
    const setUserDetails = props.setUserDetails
    const [events, setEvents] = useState([])
    const [eventsWithSessions, setEventsWithSessions] = useState([])
    const [registrations, setRegistrations] = useState([])
    const [modal, setModal] = useState(false)
    const [userDetailsModal, setUserDetailsModal] = useState(false)

    const loading = props.loading

    useEffect(() => {
        getEvents()
    }, [])

    useEffect(() => {
        if (userDetails) {
            const loadRegistrations = async () => {
                await axios.post('/api/registrations/user', userDetails.user, tokenConfig())
                    .then(res => {
                        setRegistrations(res.data)
                    })
                    .catch(err => {
                        console.log(err)
                    })
            }

            loadRegistrations()
        }
    }, [userDetails])

    const getEvents = async () => {
        try {
            const res = await axios.post('/api/events/filter', { filter: { status: 'Open' } })
            const allEvents = res.data
            setEvents(allEvents)

            const now = new Date()
            const dayBefore = new Date(now)
            dayBefore.setDate(dayBefore.getDate() - 1)
            const in3Months = new Date(now)
            in3Months.setMonth(in3Months.getMonth() + 3)

            const sessionResults = await Promise.all(
                allEvents.map(event =>
                    axios.post('/api/event-sessions/getSessionsByEventId', {
                        eventId: event._id,
                        filters: {
                            status: 'Open',
                            date: { $gt: dayBefore.toISOString(), $lte: in3Months.toISOString() },
                            registrations: { $lt: event.capacity },
                        }
                    }, tokenConfig())
                        .then(r => r.data)
                        .catch(() => [])
                )
            )

            const withSessions = allEvents
                .map((event, i) => ({
                    event,
                    sessions: [...sessionResults[i]].sort((a, b) => new Date(a.date) - new Date(b.date))
                }))
                .filter(({ sessions }) => sessions.length > 0)

            setEventsWithSessions(withSessions)
        } catch (err) {
            console.log(err)
        }
    }

    const cancelRegistration = async _id => {
        const confirm = window.confirm("Cancel your registration?")
        if (!confirm) return console.log("Cancelled Request")
        await axios.patch(`/api/registrations`, { _id, status: 'Cancelled' }, tokenConfig())
            .then(() => {
                setRegistrations(registrations.filter(registration => registration._id !== _id))
            })
            .catch(err => console.log(err))
    }

    const toggle = () => {
        setModal(!modal)
    }

    const updateProfilePhoto = newPhoto => {
        if (!userDetails) return
        setUserDetails({
            ...userDetails,
            user: {
                ...userDetails.user,
                photo: newPhoto,
            },
        })
    }

    const openEvents = useMemo(() => {
        return events.filter(event => event.status === 'Open')
    }, [events])

    const activeRegistrations = useMemo(() => {
        return registrations.filter(({ event_id, sessionId, status }) => event_id && sessionId && status !== 'Cancelled')
    }, [registrations])

    const checkedInCount = useMemo(() => {
        return activeRegistrations.filter(({ status }) => status === 'Checked In').length
    }, [activeRegistrations])

    const nextSession = useMemo(() => {
        return [...activeRegistrations]
            .filter(({ sessionId }) => sessionId?.date)
            .sort((a, b) => new Date(a.sessionId.date) - new Date(b.sessionId.date))[0] || null
    }, [activeRegistrations])

    const landingHighlights = [
        {
            icon: faLeaf,
            title: 'Bukit Kiara Federal Park',
            description: 'Also known as Taman Persekutuan Bukit Kiara, this urban forest located in Segambut is the first Federal Park in Malaysia and spans approximately 400 acres. It is one of Kuala Lumpur’s most important green lungs.',
        },
        {
            icon: faUsers,
            title: 'A community-led movement',
            description: 'The organisation\'s roots stretch back to long-running efforts to protect Bukit Kiara, with FoBK formally registered in 2014.',
        },
        {
            icon: faCalendarAlt,
            title: 'Real ways to get involved',
            description: 'FoBK encourages people to hike, volunteer, become members, support programmes, and join citizen-science activities.',
        },
    ]

    const renderStatus = status => {
        const statusClass = status.toLowerCase().replace(/\s+/g, '-')
        return <span className={`status-pill status-pill--${statusClass}`}>{status}</span>
    }

    const formatDate = value => {
        if (!value) return 'TBA'
        return new Date(value).toLocaleDateString()
    }

    if (loading) {
        return null
    }

    if (!userDetails) {
        return (
            <div className='landing-page'>
                <section className='landing-hero'>
                    <div
                        className='landing-hero__backdrop'
                        style={{ backgroundImage: `url(${Banner})` }}
                    />
                    <div className='page-shell page-shell--wide landing-hero__content'>
                        <div className='landing-hero__copy'>
                            <span className='eyebrow'>Friends of Bukit Kiara</span>
                            <h1 className='page-title'>Go hiking, learn, and help protect Bukit Kiara.</h1>
                            <p className='page-description'>
                                FoBK's main site introduces Bukit Kiara Federal Park, a roughly 400-acre urban forest and green lung for Kuala Lumpur. This portal supports that work by helping members and supporters register for upcoming walks, programmes, and community activities.
                            </p>
                            <div className='hero-actions'>
                                <a className='btn btn-primary' href='/eventregister'>Explore Events</a>
                                <a className='btn btn-outline-light' href='https://www.fobk.org/' target='_blank' rel='noreferrer'>Visit FoBK.org</a>
                            </div>
                        </div>

                    </div>
                </section>

                <section className='page-shell page-shell--wide page-section'>
                    <span className='eyebrow'>What's On</span>
                    <h2 className='section-title' style={{ marginTop: '0.2rem', marginBottom: '1.25rem' }}>Upcoming events</h2>
                    {eventsWithSessions.length === 0 ? (
                        <p style={{ color: 'var(--fobk-ink-muted)', fontStyle: 'italic' }}>No upcoming events at the moment — check back soon.</p>
                    ) : (
                        <UpcomingEventsGrid
                            upcomingEventSessions={eventsWithSessions}
                            onSelectSession={(event, session) => navigate(`/eventregister?eventId=${event._id}&sessionId=${session._id}`)}
                        />
                    )}
                </section>

                <section className='page-shell page-shell--wide page-section' id='landing-highlights'>
                    <span className='eyebrow'>About FoBK</span>
                    <h2 className='section-title'>Community action, forest education, and practical ways to help</h2>
                    <div className='feature-grid'>
                        {landingHighlights.map(({ icon, title, description }) => (
                            <div className='feature-card' key={title}>
                                <div className='feature-card__icon'>
                                    <FontAwesomeIcon icon={icon} />
                                </div>
                                <h3>{title}</h3>
                                <p>{description}</p>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        )
    }

    return (
        <Fragment>
            <UpdateDetails
                userDetails={userDetails}
                modal={userDetailsModal}
                setModal={setUserDetailsModal}
                setUserDetails={setUserDetails}
            />
            <UpdatePhoto modal={modal} toggle={toggle} _id={userDetails.user._id} updateProfilePhoto={updateProfilePhoto} />

            <section className='profile-hero'>
                <div
                    className='profile-hero__backdrop'
                    style={{ backgroundImage: `url(${Banner})` }}
                />
                <div className='page-shell page-shell--wide profile-hero__content'>
                    <div className='profile-hero__card'>
                        <button
                            className='profile-avatar-button'
                            onClick={toggle}
                            type='button'
                        >
                            <img
                                alt={`${userDetails.user.name} profile`}
                                className='profile-avatar'
                                src={userDetails.user.photo}
                            />
                        </button>

                        <div className='profile-summary'>
                            <span className='eyebrow'>Member Dashboard</span>
                            <h1 className='page-title'>{userDetails.user.name}</h1>
                            <p className='page-description'>Track your upcoming FoBK sessions, keep your member profile current, and jump back to the main site whenever you want public information, programme updates, or Bukit Kiara background.</p>

                            <div className='profile-meta-grid'>
                                <div className='profile-meta-item'>
                                    <FontAwesomeIcon icon={faEnvelope} />
                                    <div>
                                        <strong>Email</strong>
                                        <span>{userDetails.user.email}</span>
                                    </div>
                                </div>
                                {userDetails.user.phoneNumber && (
                                    <div className='profile-meta-item'>
                                        <FontAwesomeIcon icon={faPhone} />
                                        <div>
                                            <strong>Phone</strong>
                                            <span>{userDetails.user.phoneNumber}</span>
                                        </div>
                                    </div>
                                )}
                                {userDetails.user.validId && (
                                    <div className='profile-meta-item'>
                                        <FontAwesomeIcon icon={faIdCard} />
                                        <div>
                                            <strong>ID</strong>
                                            <span>{userDetails.user.validId}</span>
                                        </div>
                                    </div>
                                )}
                                {userDetails.user.dob && (
                                    <div className='profile-meta-item'>
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                        <div>
                                            <strong>Date of Birth</strong>
                                            <span>{formatDate(userDetails.user.dob)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className='profile-side-actions'>
                            <a className='btn btn-dark' href='https://www.fobk.org/' target='_blank' rel='noreferrer'>
                                Visit FoBK.org
                            </a>
                            <Button color='secondary' onClick={() => setUserDetailsModal(true)}>
                                <FontAwesomeIcon icon={faEdit} style={{ marginRight: '0.55rem' }} />
                                Edit Profile
                            </Button>
                        </div>
                    </div>

                    <div className='profile-stats-grid'>
                        <div className='metric-card'>
                            <span>Active Registrations</span>
                            <strong>{activeRegistrations.length}</strong>
                        </div>
                        <div className='metric-card'>
                            <span>Checked In</span>
                            <strong>{checkedInCount}</strong>
                        </div>
                        <div className='metric-card'>
                            <span>Open Events</span>
                            <strong>{openEvents.length}</strong>
                        </div>
                        <div className='metric-card'>
                            <span>Next Session</span>
                            <strong>{nextSession?.sessionId?.date ? new Date(nextSession.sessionId.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'TBA'}</strong>
                        </div>
                    </div>
                </div>
            </section>

            <section className='page-shell page-shell--wide page-section'>
                <div className='section-heading-row'>
                    <div>
                        <span className='eyebrow'>Your Schedule</span>
                        <h2 className='section-title'>Upcoming and active registrations</h2>
                        <p className='section-lead'>Everything you need for your next FoBK event, from dates and locations to check-in status.</p>
                    </div>

                    <div className='fobk-surface-card next-session-card'>
                        <span className='eyebrow'>Next Up</span>
                        <strong>{nextSession?.event_id?.name || 'Select an event to get started'}</strong>
                        <p>{nextSession?.sessionId?.date ? `${formatDate(nextSession.sessionId.date)} at ${nextSession.sessionId.starttime || 'TBA'}` : 'Your next booked session will appear here.'}</p>
                    </div>
                </div>

                <div className='fobk-surface-card table-shell'>
                    {activeRegistrations.length ? (
                        <Table className='fobk-data-table'>
                            <thead>
                                <tr>
                                    <th>Registered Email</th>
                                    <th>Event</th>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Difficulty</th>
                                    <th>Location</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activeRegistrations.map(({ _id, event_id, email, status, sessionId }) => {
                                    const event = event_id
                                    if (!event || !sessionId) return null
                                    return (
                                        <tr key={_id}>
                                            <td>{email}</td>
                                            <td>{event.name}</td>
                                            <td>{sessionId?.date?.substr(0, 10)}</td>
                                            <td>{sessionId?.starttime} - {sessionId?.endtime}</td>
                                            <td>{event.difficulty}</td>
                                            <td>
                                                {event.location ? (
                                                    <a href={buildLocationUrl(event.location, event.locationPlaceId)} target='_blank' rel='noreferrer'>
                                                        {locationLabel(event.location)}
                                                    </a>
                                                ) : (
                                                    <span className='text-muted'>—</span>
                                                )}
                                            </td>
                                            <td>{renderStatus(status)}</td>
                                            <td>
                                                <FontAwesomeIcon
                                                    color='red'
                                                    className='cursor'
                                                    icon={faTrash}
                                                    onClick={() => cancelRegistration(_id)}
                                                />
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                    ) : (
                        <div className='empty-state'>
                            <h3>No active registrations yet</h3>
                            <p>Browse the latest FoBK activities and reserve your place when you are ready.</p>
                            <a className='btn btn-primary mt-3' href='/eventregister'>
                                Explore Events
                                <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '0.6rem' }} />
                            </a>
                        </div>
                    )}
                </div>
            </section>
        </Fragment>
    )
}

export default ProfilePage
